import { getRuntimeEnvironment } from "../local/runtimeEnvironment";
import {
  isSupabaseConfigured,
  supabaseAnonKey,
  supabaseUrl,
} from "./config";
import { weRiderSupabase } from "./weRiderService";

const FUNCTION_NAME = "we-rider-payments";
const LOCAL_CONNECTION_KEY = "mozzo:we-rider-payment-connection";
const LOCAL_OPERATIONS_KEY = "mozzo:we-rider-payment-operations";
const isLocalPreview = getRuntimeEnvironment() === "development";

const readJson = (key, fallback) => {
  if (typeof window === "undefined") return fallback;
  try {
    const value = JSON.parse(window.localStorage.getItem(key) || "null");
    return value ?? fallback;
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) => {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(key, JSON.stringify(value));
  }
  return value;
};

const normalizeConnection = (value = {}) => ({
  connected: value?.connected === true,
  status: String(value?.status || "not_connected"),
  accountReference: String(value?.accountReference || ""),
});

const invokePayments = async (body, fallbackMessage) => {
  if (!isSupabaseConfigured || !weRiderSupabase) {
    throw new Error("Mercado Pago no esta configurado en este entorno.");
  }
  const { data: sessionData, error: sessionError } =
    await weRiderSupabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (sessionError || !accessToken) {
    throw new Error("Tu sesion de Rider vencio. Vuelve a ingresar.");
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 25000);
  try {
    const response = await fetch(
      `${supabaseUrl}/functions/v1/${FUNCTION_NAME}`,
      {
        method: "POST",
        headers: {
          apikey: supabaseAnonKey,
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
        signal: controller.signal,
        cache: "no-store",
      }
    );
    const data = await response.json().catch(() => null);
    if (!response.ok || !data || data.error) {
      throw new Error(data?.error || data?.message || fallbackMessage);
    }
    return data;
  } catch (error) {
    if (error?.name === "AbortError") {
      throw new Error("Mercado Pago tardo demasiado en responder. Reintenta.");
    }
    throw new Error(error?.message || fallbackMessage);
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const buildLocalOperation = ({
  assignmentId,
  type,
  method,
  status = "approved",
  checkoutUrl = "",
}) => {
  const operations = readJson(LOCAL_OPERATIONS_KEY, {});
  const operation = {
    id: `local-${type}-${assignmentId}`,
    type,
    method,
    status,
    amount: 0,
    marketplaceFee: 0,
    checkoutUrl,
    qrData:
      method === "qr"
        ? `00020101021226820014AR.COM.MOZZOAPP0112WE-RIDER-QR5204421553030325802AR5909WE RIDER6009CORRIENTES6304ABCD`
        : "",
    approvedAt: status === "approved" ? new Date().toISOString() : null,
  };
  writeJson(LOCAL_OPERATIONS_KEY, {
    ...operations,
    [`${assignmentId}:${type}`]: operation,
  });
  return operation;
};

export const getWeRiderPaymentConnection = async () => {
  if (isLocalPreview) {
    return normalizeConnection(readJson(LOCAL_CONNECTION_KEY, {}));
  }
  const data = await invokePayments(
    { action: "connection_status" },
    "No pudimos consultar la cuenta de Mercado Pago del Rider."
  );
  return normalizeConnection(data.connection);
};

export const startWeRiderMercadoPagoConnection = async () => {
  if (isLocalPreview) {
    return {
      local: true,
      connection: writeJson(LOCAL_CONNECTION_KEY, {
        connected: true,
        status: "connected",
        accountReference: "Cuenta Rider de prueba",
      }),
    };
  }
  const redirectUrl = `${window.location.origin}/we-pedidos/rider/mercado-pago/callback`;
  return invokePayments(
    { action: "oauth_start", redirectUrl },
    "No pudimos iniciar la conexion con Mercado Pago."
  );
};

export const completeWeRiderMercadoPagoConnection = async ({ code, state }) => {
  const data = await invokePayments(
    { action: "oauth_callback", code, state },
    "No pudimos completar la conexion con Mercado Pago."
  );
  return normalizeConnection(data.connection);
};

export const disconnectWeRiderMercadoPago = async () => {
  if (isLocalPreview) {
    window.localStorage.removeItem(LOCAL_CONNECTION_KEY);
    return normalizeConnection();
  }
  const data = await invokePayments(
    { action: "oauth_disconnect" },
    "No pudimos desconectar Mercado Pago."
  );
  return normalizeConnection(data.connection);
};

export const quoteWeRiderPickupPayment = async (assignmentId, method) => {
  if (isLocalPreview) return { hasCashPromotion: false };
  const data = await invokePayments({ action: "quote_pickup_payment", assignmentId, method }, "No pudimos consultar el importe del restaurante.");
  return data.quote;
};

export const createWeRiderPickupDeeplink = async (assignmentId, confirmation = {}) => {
  if (isLocalPreview) {
    const operation = buildLocalOperation({
      assignmentId,
      type: "pickup_settlement",
      method: "deeplink",
      status: "pending",
      checkoutUrl: "https://www.mercadopago.com.ar/",
    });
    return operation;
  }
  const data = await invokePayments(
    {
      action: "create_pickup_deeplink",
      expectedTotal: confirmation.expectedTotal,
      customerPriceConfirmed: confirmation.customerPriceConfirmed === true,
      assignmentId,
      returnUrl: `${window.location.origin}/we-pedidos/rider?payment=pickup`,
    },
    "No pudimos crear el pago al restaurante."
  );
  return data.operation;
};

export const recordWeRiderPickupCash = async (assignmentId, confirmation = {}) => {
  if (isLocalPreview) {
    return buildLocalOperation({
      assignmentId,
      type: "pickup_settlement",
      method: "cash",
    });
  }
  const data = await invokePayments(
    { action: "record_pickup_cash", assignmentId, expectedTotal: confirmation.expectedTotal,
      customerPriceConfirmed: confirmation.customerPriceConfirmed === true },
    "No pudimos registrar el pago en efectivo al restaurante."
  );
  return data.operation;
};

export const recordWeRiderRestaurantDeliveryFeeCash = async (assignmentId) => {
  if (isLocalPreview) {
    return buildLocalOperation({
      assignmentId,
      type: "restaurant_delivery_fee_collection",
      method: "cash",
    });
  }
  const data = await invokePayments(
    { action: "record_restaurant_delivery_fee_cash", assignmentId },
    "No pudimos registrar el cobro del envio en la caja del restaurante."
  );
  return data.operation;
};

export const createWeRiderCollectionQr = async (assignmentId) => {
  if (isLocalPreview) {
    return buildLocalOperation({
      assignmentId,
      type: "customer_collection",
      method: "qr",
      status: "pending",
    });
  }
  const data = await invokePayments(
    { action: "create_collection_qr", assignmentId },
    "No pudimos generar el QR de cobro del Rider."
  );
  return data.operation;
};

export const recordWeRiderCollectionCash = async (assignmentId) => {
  if (isLocalPreview) {
    return buildLocalOperation({
      assignmentId,
      type: "customer_collection",
      method: "cash",
    });
  }
  const data = await invokePayments(
    { action: "record_collection_cash", assignmentId },
    "No pudimos registrar el cobro en efectivo."
  );
  return data.operation;
};

export const refreshWeRiderPaymentOperation = async (
  assignmentId,
  operationType
) => {
  if (isLocalPreview) {
    const operations = readJson(LOCAL_OPERATIONS_KEY, {});
    return operations[`${assignmentId}:${operationType}`] || null;
  }
  const data = await invokePayments(
    { action: "refresh_operation", assignmentId, operationType },
    "No pudimos actualizar el estado del pago."
  );
  return data.operation;
};

export const approveLocalWeRiderPayment = (assignmentId, operationType) => {
  if (!isLocalPreview) return null;
  const operations = readJson(LOCAL_OPERATIONS_KEY, {});
  const key = `${assignmentId}:${operationType}`;
  if (!operations[key]) return null;
  const operation = {
    ...operations[key],
    status: "approved",
    approvedAt: new Date().toISOString(),
  };
  writeJson(LOCAL_OPERATIONS_KEY, { ...operations, [key]: operation });
  return operation;
};

export const isLocalWeRiderPaymentsPreview = () => isLocalPreview;
